import { useEffect, useMemo, useRef, useState, type FormEvent, Fragment } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Loader2, RefreshCw } from 'lucide-react';
import { Badge, type BadgeVariant } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input, Select } from '@/components/ui/Input';
import { Table, TableCell, TableHead, TableHeader, TableRow, TableWrap } from '@/components/ui/Table';
import { useToast } from '@/components/ui/Toast';
import { useAuth } from '@/contexts/AuthContext';
import { AMC_TERMS_REQUIRED_MESSAGE, AMC_TERMS_REQUIRED_REASON } from '@/api/amcTerms';
import { adsApi } from '@/api/client';
import {
  CART_ADDERS_QUERY,
  RULE_LOOKBACK_DAYS_DEFAULT,
  RULE_LOOKBACK_DAYS_MAX,
  RULE_REFRESH_DAYS_MAX,
  SEARCH_TERM_PLACEHOLDER,
  audienceFailureCopy,
  buildSearchAudienceQuery,
  isPlaceholderSearchTerm,
} from '@/components/audience/audienceForm';
import { cn } from '@/utils/cn';
import type {
  AmcAdvertiserType,
  AmcAudience,
  AmcLookalikeReach,
  CreateAmcAudienceBody,
  CreateAmcLookalikeBody,
} from '@/types/amcAudiences';

const QUERY_TEMPLATES: Array<{ id: string; label: string; query: string }> = [
  {
    id: 'cart',
    label: 'Cart adders',
    query: CART_ADDERS_QUERY,
  },
  {
    id: 'search',
    label: 'Sponsored ads search term',
    query: '',
  },
  {
    id: 'detail',
    label: 'Product detail viewers',
    query:
      "SELECT user_id FROM amazon_attributed_events_by_traffic_time_for_audiences WHERE conversion_event_subtype = 'detailPageView'",
  },
  { id: 'custom', label: 'Custom SQL', query: '' },
];

const LOOKALIKE_REACH: Array<{ id: AmcLookalikeReach; label: string }> = [
  { id: 'MOST_SIMILAR', label: 'Most similar' },
  { id: 'SIMILAR', label: 'Similar' },
  { id: 'BALANCED', label: 'Balanced' },
  { id: 'BROAD', label: 'Broad' },
  { id: 'MOST_BROAD', label: 'Most broad' },
];

const LOOKALIKE_COUNTRIES = [
  'US',
  'UK',
  'CA',
  'DE',
  'FR',
  'IT',
  'ES',
  'IN',
  'JP',
  'AU',
  'MX',
  'BR',
  'NL',
  'SE',
  'AE',
  'SA',
  'TR',
];

const READY_INSTANCE_STATUSES = new Set(['COMPLETED', 'ACTIVE', '']);
const IN_FLIGHT_STATUSES = new Set(['PENDING', 'RUNNING']);

function extractError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const detail = err.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (detail && typeof detail === 'object') {
      const amazonStatus = (detail as { amazon_status?: unknown }).amazon_status;
      if (amazonStatus === 401 || amazonStatus === 403) {
        return 'AMC is not available for this account. Confirm AMC is provisioned and re-authorize Amazon Ads with the audiences scope.';
      }
      if (typeof (detail as { message?: unknown }).message === 'string') {
        return (detail as { message: string }).message;
      }
      if (typeof (detail as { error?: unknown }).error === 'string') {
        return (detail as { error: string }).error;
      }
    }
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return 'Something went wrong';
}

function statusBadgeVariant(status: string | null): BadgeVariant {
  const value = (status || '').toUpperCase();
  if (value === 'SUCCEEDED' || value === 'COMPLETED' || value === 'ACTIVE') return 'success';
  if (value === 'PENDING' || value === 'RUNNING') return 'warning';
  if (value === 'FAILED') return 'danger';
  if (value === 'DEACTIVATED' || value === 'DELETED') return 'archived';
  return 'neutral';
}

function formatWhen(value: string | null | undefined): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
}

function formatAudienceType(value: string | null): string {
  const normalized = (value || '').toUpperCase();
  if (normalized === 'LOOKALIKE') return 'Lookalike';
  if (normalized === 'RULE_BASED' || !normalized) return 'Rule-based';
  return value || '—';
}

function isInstanceReady(status: string | null | undefined): boolean {
  return READY_INSTANCE_STATUSES.has((status || '').toUpperCase());
}

function activationLabel(row: AmcAudience): string {
  const canonical = row.dspCanonicalId?.trim();
  const dspId = row.dspAudienceId?.trim();
  if (canonical && dspId && canonical !== dspId) return `${canonical} / ${dspId}`;
  return canonical || dspId || 'Not activated';
}

function needsReauthorize(reason: string | null | undefined, amazonStatus: number | null | undefined): boolean {
  if (reason !== 'amc_unavailable') return false;
  return amazonStatus === 401 || amazonStatus === 403 || amazonStatus == null;
}

export function AudiencePage() {
  const { profileId: profileIdParam } = useParams<{ profileId: string }>();
  const profileId = profileIdParam ? decodeURIComponent(profileIdParam) : '';
  const { hasPermission } = useAuth();
  const canWrite = hasPermission('profiles.write');
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const autoSynced = useRef(false);

  const [instanceId, setInstanceId] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createKind, setCreateKind] = useState<'rule' | 'lookalike'>('rule');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [advertiserType, setAdvertiserType] = useState<AmcAdvertiserType>('SPONSORED_ADS');
  const [templateId, setTemplateId] = useState(QUERY_TEMPLATES[0].id);
  const [querySql, setQuerySql] = useState(QUERY_TEMPLATES[0].query);
  const [lookbackDays, setLookbackDays] = useState(String(RULE_LOOKBACK_DAYS_DEFAULT));
  const [refreshRateDays, setRefreshRateDays] = useState('7');
  const [searchTerm, setSearchTerm] = useState(SEARCH_TERM_PLACEHOLDER);
  const [countryCode, setCountryCode] = useState('US');
  const [lookalikeReach, setLookalikeReach] = useState<AmcLookalikeReach>('BALANCED');

  const contextQuery = useQuery({
    queryKey: ['amc-context', profileId],
    queryFn: async () => {
      const res = await adsApi.getAmcContext(profileId);
      if (!res.success || !res.data) throw new Error(res.message || 'Failed to load AMC context');
      return res.data;
    },
    enabled: Boolean(profileId),
  });

  const instances = contextQuery.data?.instances ?? [];
  const syncState = contextQuery.data?.sync;

  useEffect(() => {
    if (!instances.length) {
      setInstanceId('');
      return;
    }
    setInstanceId((current) =>
      current && instances.some((row) => row.instanceId === current) ? current : instances[0].instanceId
    );
  }, [instances]);

  useEffect(() => {
    const suggested = contextQuery.data?.defaultCountryCode;
    if (suggested && LOOKALIKE_COUNTRIES.includes(suggested)) {
      setCountryCode(suggested);
    }
  }, [contextQuery.data?.defaultCountryCode]);

  const audiencesQuery = useQuery({
    queryKey: ['amc-audiences', profileId, instanceId],
    queryFn: async () => {
      const res = await adsApi.getAmcAudiences(profileId, instanceId || undefined);
      if (!res.success || !res.data) throw new Error(res.message || 'Failed to load audiences');
      return res.data.audiences;
    },
    enabled: Boolean(profileId) && Boolean(instanceId),
  });

  const audiences = audiencesQuery.data ?? [];
  const inflightIds = useMemo(
    () =>
      audiences
        .filter((row) => IN_FLIGHT_STATUSES.has((row.status || '').toUpperCase()))
        .map((row) => row.audienceExecutionId),
    [audiences]
  );

  useQuery({
    queryKey: ['amc-audience-poll', profileId, instanceId, inflightIds.join(',')],
    queryFn: async () => {
      const refreshed = await Promise.all(
        inflightIds.map(async (executionId) => {
          const res = await adsApi.getAmcAudience(profileId, executionId, instanceId || undefined);
          if (!res.success || !res.data) return null;
          return res.data;
        })
      );
      const byId = new Map(
        refreshed.filter((row): row is AmcAudience => Boolean(row)).map((row) => [row.audienceExecutionId, row])
      );
      if (byId.size) {
        queryClient.setQueryData<AmcAudience[]>(['amc-audiences', profileId, instanceId], (current) =>
          (current ?? []).map((row) => byId.get(row.audienceExecutionId) ?? row)
        );
      }
      return true;
    },
    enabled: Boolean(profileId) && Boolean(instanceId) && inflightIds.length > 0,
    refetchInterval: 5000,
  });

  const syncMutation = useMutation({
    mutationFn: async () => {
      const res = await adsApi.syncAmcCatalog(profileId);
      if (!res.success || !res.data) throw new Error(res.message || 'Failed to sync AMC');
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['amc-context', profileId], data);
      queryClient.invalidateQueries({ queryKey: ['amc-audiences', profileId] });
      const reason = data.sync?.reason || data.reason;
      if (reason === AMC_TERMS_REQUIRED_REASON) {
        showToast(AMC_TERMS_REQUIRED_MESSAGE, 'error');
        return;
      }
      if (reason === 'amc_unavailable' || data.sync?.amazonStatus === 401 || data.sync?.amazonStatus === 403) {
        showToast(
          'AMC is not available yet. Re-authorize Amazon Ads with the audiences scope if this account is provisioned.',
          'error'
        );
        return;
      }
      if (reason === 'no_amc_account') {
        showToast('No Amazon Marketing Cloud account is provisioned for this profile.', 'error');
        return;
      }
      showToast('AMC catalog refreshed', 'success');
    },
    onError: (err: unknown) => {
      showToast(extractError(err), 'error');
    },
  });

  useEffect(() => {
    if (!profileId || autoSynced.current || !contextQuery.isSuccess || syncMutation.isPending) return;
    const sync = contextQuery.data.sync;
    const neverSynced = !sync?.lastSyncedAt || sync.status === 'pending';
    if (neverSynced && (contextQuery.data.instances ?? []).length === 0) {
      autoSynced.current = true;
      syncMutation.mutate();
    }
  }, [profileId, contextQuery.isSuccess, contextQuery.data, syncMutation]);

  const createMutation = useMutation({
    mutationFn: async (payload: { kind: 'rule' | 'lookalike'; body: CreateAmcAudienceBody | CreateAmcLookalikeBody }) => {
      const res =
        payload.kind === 'lookalike'
          ? await adsApi.createAmcLookalike(profileId, payload.body as CreateAmcLookalikeBody)
          : await adsApi.createAmcAudience(profileId, payload.body as CreateAmcAudienceBody);
      if (!res.success || !res.data) throw new Error(res.message || 'Failed to create audience');
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['amc-audiences', profileId] });
      showToast('Audience creation started', 'success');
      setShowCreate(false);
      setName('');
      setDescription('');
    },
    onError: (err: unknown) => {
      showToast(extractError(err), 'error');
    },
  });

  const retryMutation = useMutation({
    mutationFn: async (audience: AmcAudience) => {
      if (!audience.query?.trim() || !audience.audienceName?.trim()) {
        throw new Error('This audience is missing a name or SQL query, so it cannot be retried.');
      }
      const res = await adsApi.updateAmcAudience(
        profileId,
        audience.audienceExecutionId,
        {
          audienceName: audience.audienceName.trim(),
          audienceDescription: audience.audienceDescription || undefined,
          advertiserId: audience.advertiserId || undefined,
          advertiserType: (audience.advertiserType as AmcAdvertiserType) || 'SPONSORED_ADS',
          query: audience.query.trim(),
          timeWindowStart: audience.timeWindowStart || undefined,
          timeWindowEnd: audience.timeWindowEnd || undefined,
          refreshRateDays: audience.refreshRateDays ?? 7,
          timeWindowRelative: audience.timeWindowRelative ?? true,
        },
        audience.instanceId
      );
      if (!res.success || !res.data) throw new Error(res.message || 'Failed to retry audience');
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['amc-audiences', profileId] });
      showToast('Audience retry started', 'success');
    },
    onError: (err: unknown) => {
      showToast(extractError(err), 'error');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (audience: AmcAudience) => {
      const res = await adsApi.deleteAmcAudience(
        profileId,
        audience.audienceExecutionId,
        audience.instanceId
      );
      if (!res.success) throw new Error(res.message || 'Failed to delete audience');
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['amc-audiences', profileId] });
      showToast('Audience deleted', 'success');
    },
    onError: (err: unknown) => {
      showToast(extractError(err), 'error');
    },
  });

  const onTemplateChange = (id: string) => {
    setTemplateId(id);
    if (id === 'search') {
      setQuerySql(buildSearchAudienceQuery(searchTerm));
      return;
    }
    const template = QUERY_TEMPLATES.find((row) => row.id === id);
    if (template) setQuerySql(template.query);
  };

  const onSearchTermChange = (value: string) => {
    setSearchTerm(value);
    setQuerySql(buildSearchAudienceQuery(value));
  };

  const onCreateKindChange = (kind: 'rule' | 'lookalike') => {
    setCreateKind(kind);
    if (kind === 'lookalike') {
      setRefreshRateDays('21');
      setLookbackDays('21');
    } else {
      setRefreshRateDays('7');
      setLookbackDays(String(RULE_LOOKBACK_DAYS_DEFAULT));
    }
  };

  const resolvedAdvertiserId = useMemo(() => {
    if (advertiserType === 'DISPLAY') return contextQuery.data?.dspAdvertiserId || undefined;
    return contextQuery.data?.profileAdvertiserId || undefined;
  }, [advertiserType, contextQuery.data?.dspAdvertiserId, contextQuery.data?.profileAdvertiserId]);

  const onCreate = (event: FormEvent) => {
    event.preventDefault();
    if (!instanceId) {
      showToast('Select an AMC instance first', 'error');
      return;
    }
    const lookback = Number(lookbackDays);
    const refresh = Number(refreshRateDays);
    if (!name.trim() || !querySql.trim()) {
      showToast('Name and SQL query are required', 'error');
      return;
    }
    if (!Number.isFinite(lookback) || lookback < 1 || lookback > RULE_LOOKBACK_DAYS_MAX) {
      showToast(`Lookback days must be between 1 and ${RULE_LOOKBACK_DAYS_MAX}`, 'error');
      return;
    }
    if (!Number.isFinite(refresh) || refresh < 1) {
      showToast('Refresh days must be at least 1', 'error');
      return;
    }
    if (templateId === 'search' && (!searchTerm.trim() || isPlaceholderSearchTerm(searchTerm))) {
      showToast('Replace the chai placeholder with a search term from this account.', 'error');
      return;
    }
    if (createKind === 'lookalike') {
      if (refresh < 7 || refresh > 21) {
        showToast('Lookalike refresh days must be between 7 and 21', 'error');
        return;
      }
      if (!countryCode.trim()) {
        showToast('Country is required for lookalike audiences', 'error');
        return;
      }
    } else if (refresh > RULE_REFRESH_DAYS_MAX) {
      showToast(`Refresh days must be between 1 and ${RULE_REFRESH_DAYS_MAX}`, 'error');
      return;
    }
    if (createKind === 'lookalike') {
      createMutation.mutate({
        kind: 'lookalike',
        body: {
          instanceId,
          audienceName: name.trim(),
          audienceDescription: description.trim() || undefined,
          advertiserId: resolvedAdvertiserId,
          query: querySql.trim(),
          countryCode,
          lookalikeAudienceExpectedReach: lookalikeReach,
          lookbackDays: lookback,
          refreshRateDays: refresh,
          timeWindowRelative: true,
        },
      });
      return;
    }
    createMutation.mutate({
      kind: 'rule',
      body: {
        instanceId,
        audienceName: name.trim(),
        audienceDescription: description.trim() || undefined,
        advertiserId: resolvedAdvertiserId,
        advertiserType,
        query: querySql.trim(),
        lookbackDays: lookback,
        refreshRateDays: refresh,
        timeWindowRelative: true,
      },
    });
  };

  const loading =
    contextQuery.isLoading ||
    syncMutation.isPending ||
    (Boolean(instanceId) && audiencesQuery.isLoading);
  const error = contextQuery.error
    ? extractError(contextQuery.error)
    : audiencesQuery.error
      ? extractError(audiencesQuery.error)
      : null;
  const selectedInstance = useMemo(
    () => instances.find((row) => row.instanceId === instanceId) ?? null,
    [instances, instanceId]
  );
  const readyInstance = selectedInstance ? isInstanceReady(selectedInstance.creationStatus) : false;
  const reauthorize = needsReauthorize(syncState?.reason, syncState?.amazonStatus);
  const termsRequired = syncState?.reason === AMC_TERMS_REQUIRED_REASON;

  const emptyMessage = (() => {
    if (instances.length > 0) return null;
    if (reauthorize) {
      return 'AMC is not available for this account. Re-authorize Amazon Ads with the advertising::audiences scope, then sync again.';
    }
    if (syncState?.reason === 'no_amc_account') {
      return 'No Amazon Marketing Cloud account is provisioned for this advertising profile.';
    }
    if (syncState?.reason === 'missing_amc_headers') {
      return 'AMC account metadata is incomplete (entity or marketplace id missing). Re-sync after confirming the Amazon Ads connection.';
    }
    if (syncState?.reason === 'amc_unavailable') {
      return 'Amazon Marketing Cloud is not available for this profile yet.';
    }
    return 'No AMC instance has synced for this profile yet. Use Sync now once Amazon Marketing Cloud is provisioned.';
  })();

  return (
    <div className="mx-auto max-w-7xl space-y-5 bg-page px-4 py-6 min-h-full">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-headline-md font-semibold text-foreground">Audience</h1>
          <p className="mt-1 text-sm text-foreground-muted">
            Amazon Marketing Cloud rule-based and lookalike audiences for this advertising profile.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="secondary"
            onClick={() => syncMutation.mutate()}
            disabled={syncMutation.isPending}
          >
            <RefreshCw className={cn('h-4 w-4', syncMutation.isPending && 'animate-spin')} aria-hidden />
            {syncMutation.isPending ? 'Syncing…' : 'Sync now'}
          </Button>
          {canWrite && instances.length > 0 && readyInstance && !termsRequired ? (
            <Button onClick={() => setShowCreate((open) => !open)}>
              {showCreate ? 'Close form' : 'Create audience'}
            </Button>
          ) : null}
        </div>
      </div>

      {selectedInstance ? (
        <div className="rounded-xl border border-border bg-surface px-4 py-3 text-sm text-foreground">
          <div className="flex flex-wrap items-end gap-4">
            {instances.length > 1 ? (
              <label className="min-w-[12rem] flex-1">
                <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                  AMC instance
                </span>
                <Select value={instanceId} onChange={(event) => setInstanceId(event.target.value)}>
                  {instances.map((row) => (
                    <option key={row.instanceId} value={row.instanceId}>
                      {row.instanceName || row.instanceId}
                    </option>
                  ))}
                </Select>
              </label>
            ) : (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-foreground-muted">AMC instance</p>
                <p className="mt-1 font-medium">{selectedInstance.instanceName || selectedInstance.instanceId}</p>
              </div>
            )}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-foreground-muted">Instance id</p>
              <p className="mt-1 font-mono text-xs">{selectedInstance.instanceId}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-foreground-muted">Type</p>
              <p className="mt-1">{selectedInstance.instanceType || '—'}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-foreground-muted">Customer</p>
              <p className="mt-1">{selectedInstance.customerCanonicalName || '—'}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-foreground-muted">Status</p>
              <p className="mt-1">{selectedInstance.creationStatus || 'Unknown'}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-foreground-muted">Advertiser types</p>
              <p className="mt-1">
                {selectedInstance.advertiserTypes.length
                  ? selectedInstance.advertiserTypes.join(', ')
                  : '—'}
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-foreground-muted">Marketplace</p>
              <p className="mt-1">{selectedInstance.marketplaceId || contextQuery.data?.marketplaceId || '—'}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-foreground-muted">Created</p>
              <p className="mt-1">{formatWhen(selectedInstance.creationDatetime)}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-foreground-muted">Last synced</p>
              <p className="mt-1">{formatWhen(syncState?.lastSyncedAt || selectedInstance.amazonSyncedAt)}</p>
            </div>
            {selectedInstance.optionalDatasets?.length ? (
              <div className="min-w-full">
                <p className="text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                  Optional datasets
                </p>
                <p className="mt-1 text-foreground-muted">
                  {selectedInstance.optionalDatasets
                    .map((dataset) => dataset.label)
                    .filter((label): label is string => Boolean(label))
                    .join(', ') || '—'}
                </p>
              </div>
            ) : null}
          </div>
          {!readyInstance ? (
            <p className="mt-3 text-foreground-muted">
              This instance is still creating. Audiences become available after Amazon marks it COMPLETED.
            </p>
          ) : null}
        </div>
      ) : null}

      {showCreate && canWrite && !termsRequired ? (
        <form
          onSubmit={onCreate}
          className="space-y-4 rounded-xl border border-border bg-surface px-5 py-4"
        >
          <div className="flex gap-2">
            <Button
              type="button"
              variant={createKind === 'rule' ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => onCreateKindChange('rule')}
            >
              Rule-based
            </Button>
            <Button
              type="button"
              variant={createKind === 'lookalike' ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => onCreateKindChange('lookalike')}
            >
              Lookalike
            </Button>
          </div>
          <p className="text-xs text-foreground-muted">
            {createKind === 'lookalike'
              ? 'Lookalike audiences need a seed SQL query that selects user_id, a country, and a reach setting. Amazon typically requires about 500 users to activate.'
              : 'Rule-based queries must SELECT user_id from AMC *_for_audiences tables. Amazon typically requires about 2,000 users to activate.'}
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <label>
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                Name
              </span>
              <Input value={name} onChange={(event) => setName(event.target.value)} required />
            </label>
            {createKind === 'rule' ? (
              <label>
                <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                  Advertiser type
                </span>
                <Select
                  className="w-full"
                  value={advertiserType}
                  onChange={(event) => setAdvertiserType(event.target.value as AmcAdvertiserType)}
                >
                  <option value="SPONSORED_ADS">Sponsored Ads</option>
                  <option value="DISPLAY">DSP / Display</option>
                </Select>
              </label>
            ) : (
              <label>
                <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                  Expected reach
                </span>
                <Select
                  className="w-full"
                  value={lookalikeReach}
                  onChange={(event) => setLookalikeReach(event.target.value as AmcLookalikeReach)}
                >
                  {LOOKALIKE_REACH.map((row) => (
                    <option key={row.id} value={row.id}>
                      {row.label}
                    </option>
                  ))}
                </Select>
              </label>
            )}
          </div>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-foreground-muted">
              Description
            </span>
            <Input value={description} onChange={(event) => setDescription(event.target.value)} />
          </label>
          <div className={cn('grid gap-3', createKind === 'lookalike' ? 'sm:grid-cols-4' : 'sm:grid-cols-3')}>
            <label>
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                SQL template
              </span>
              <Select
                className="w-full"
                value={templateId}
                onChange={(event) => onTemplateChange(event.target.value)}
              >
                {QUERY_TEMPLATES.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.label}
                  </option>
                ))}
              </Select>
            </label>
            {createKind === 'lookalike' ? (
              <label>
                <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                  Country
                </span>
                <Select
                  className="w-full"
                  value={countryCode}
                  onChange={(event) => setCountryCode(event.target.value)}
                >
                  {LOOKALIKE_COUNTRIES.map((code) => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ))}
                </Select>
              </label>
            ) : null}
            {templateId === 'search' ? (
              <label>
                <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                  Search term
                </span>
                <Input
                  value={searchTerm}
                  placeholder={SEARCH_TERM_PLACEHOLDER}
                  onChange={(event) => onSearchTermChange(event.target.value)}
                />
              </label>
            ) : null}
            <label>
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                Lookback days
              </span>
              <Input
                type="number"
                min="1"
                max={String(RULE_LOOKBACK_DAYS_MAX)}
                value={lookbackDays}
                onChange={(event) => setLookbackDays(event.target.value)}
              />
            </label>
            <label>
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                Refresh days
              </span>
              <Input
                type="number"
                min={createKind === 'lookalike' ? '7' : '1'}
                max={String(RULE_REFRESH_DAYS_MAX)}
                value={refreshRateDays}
                onChange={(event) => setRefreshRateDays(event.target.value)}
              />
            </label>
          </div>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-foreground-muted">
              Query
            </span>
            <textarea
              value={querySql}
              onChange={(event) => setQuerySql(event.target.value)}
              rows={18}
              className={cn(
                'w-full rounded-lg border border-border bg-surface px-3 py-2 font-mono text-sm text-foreground shadow-sm',
                'placeholder:text-foreground-subtle focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30'
              )}
              required
            />
          </label>
          <Button type="submit" disabled={createMutation.isPending}>
            {createMutation.isPending ? 'Creating…' : 'Create audience'}
          </Button>
        </form>
      ) : null}

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-24 text-foreground-muted">
          <Loader2 className="h-6 w-6 animate-spin" aria-hidden />
          Loading audiences…
        </div>
      ) : null}

      {error ? (
        <div className="rounded-xl border border-error/20 bg-error-subtle px-4 py-6 text-center">
          <p className="text-sm text-error-ink">{error}</p>
          <button
            type="button"
            onClick={() => {
              contextQuery.refetch();
              audiencesQuery.refetch();
            }}
            className="mt-3 text-sm font-semibold text-brand hover:underline"
          >
            Retry
          </button>
        </div>
      ) : null}

      {!loading && !error && termsRequired ? (
        <div className="rounded-xl border border-error/20 bg-error-subtle px-4 py-4 text-sm text-error-ink">
          {AMC_TERMS_REQUIRED_MESSAGE}
        </div>
      ) : null}

      {!loading && !error && instances.length === 0 && !termsRequired ? (
        <div className="rounded-xl border border-border bg-surface px-4 py-10 text-center text-sm text-foreground-muted">
          <p>{emptyMessage}</p>
          {reauthorize ? (
            <Link to="/accounts" className="mt-3 inline-block text-sm font-semibold text-brand hover:underline">
              Re-authorize Amazon Ads
            </Link>
          ) : null}
        </div>
      ) : null}

      {!loading && !error && instances.length > 0 && readyInstance ? (
        <TableWrap className="rounded-xl border border-border bg-surface">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Advertiser</TableHead>
                <TableHead>Refresh</TableHead>
                <TableHead>Last refreshed</TableHead>
                <TableHead>Activation</TableHead>
                {canWrite ? <TableHead> </TableHead> : null}
              </TableRow>
            </TableHeader>
            <tbody>
              {audiences.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canWrite ? 8 : 7} className="py-10 text-center text-foreground-muted">
                    No rule-based or lookalike audiences in this instance.
                  </TableCell>
                </TableRow>
              ) : (
                audiences.map((row) => {
                  const open = expandedId === row.audienceExecutionId;
                  const failed = (row.status || '').toUpperCase() === 'FAILED';
                  const failureCopy = audienceFailureCopy(row.statusReason);
                  return (
                    <Fragment key={row.audienceExecutionId}>
                      <TableRow
                        className="cursor-pointer"
                        onClick={() =>
                          setExpandedId(open ? null : row.audienceExecutionId)
                        }
                      >
                        <TableCell className="font-medium">{row.audienceName || '—'}</TableCell>
                        <TableCell>{formatAudienceType(row.audienceType)}</TableCell>
                        <TableCell>
                          <Badge variant={statusBadgeVariant(row.status)} className="normal-case tracking-normal">
                            {row.status || 'Unknown'}
                          </Badge>
                        </TableCell>
                        <TableCell>{row.advertiserType || '—'}</TableCell>
                        <TableCell className="tabular-nums">
                          {row.refreshRateDays == null ? '—' : `${row.refreshRateDays}d`}
                        </TableCell>
                        <TableCell>{formatWhen(row.lastRefreshedTime)}</TableCell>
                        <TableCell className="max-w-[14rem] truncate font-mono text-xs">
                          {activationLabel(row)}
                        </TableCell>
                        {canWrite ? (
                          <TableCell>
                            <div className="flex items-center gap-1">
                              {failed ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  disabled={retryMutation.isPending}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    retryMutation.mutate(row);
                                  }}
                                >
                                  Retry
                                </Button>
                              ) : null}
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={deleteMutation.isPending}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  if (
                                    window.confirm(
                                      `Delete audience “${row.audienceName || row.audienceExecutionId}”?`
                                    )
                                  ) {
                                    deleteMutation.mutate(row);
                                  }
                                }}
                              >
                                Delete
                              </Button>
                            </div>
                          </TableCell>
                        ) : null}
                      </TableRow>
                      {open ? (
                        <TableRow key={`${row.audienceExecutionId}-sql`}>
                          <TableCell colSpan={canWrite ? 8 : 7} className="bg-surface-muted">
                            <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                              SQL
                            </p>
                            <pre className="whitespace-pre-wrap font-mono text-xs text-foreground">
                              {row.query || '—'}
                            </pre>
                            {row.audienceDescription ? (
                              <p className="mt-2 text-sm text-foreground-muted">{row.audienceDescription}</p>
                            ) : null}
                            <p className="mt-2 text-xs text-foreground-muted">
                              Window: {formatWhen(row.timeWindowStart)} → {formatWhen(row.timeWindowEnd)}
                              {row.timeWindowRelative ? ' (relative)' : ''}
                              {row.countryCode ? ` · ${row.countryCode}` : ''}
                              {row.lookalikeAudienceExpectedReach
                                ? ` · ${row.lookalikeAudienceExpectedReach}`
                                : ''}
                            </p>
                            {failureCopy ? (
                              <p className="mt-2 text-sm text-foreground-muted">{failureCopy}</p>
                            ) : row.statusReason || row.audienceExecutionDescription ? (
                              <p className="mt-2 text-sm text-foreground-muted">
                                {row.statusReason || row.audienceExecutionDescription}
                              </p>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </Fragment>
                  );
                })
              )}
            </tbody>
          </Table>
        </TableWrap>
      ) : null}
    </div>
  );
}
